/**
 * Interaction Feed Columns Component
 *
 * This module defines the column configuration for the interaction feed table/grid.
 * It exports a factory function `createInteractionTableColumns` that generates column definitions
 * for displaying interaction data in a structured format.
 *
 * The columns include:
 * - Type: Shows the interaction type with a dedicated cell component
 * - Interaction: The main interaction content using InteractionListItem
 * - Date: When the interaction occurred
 * - Contact: Associated contact information
 * - Author: Who created the interaction
 *
 * Each column can be customized through props to control visibility and display options.
 */

import classNames from 'classnames';
import Interaction from 'apps/interactions/components/interaction';
import InteractionContactCell from 'apps/interactions/components/cells/interaction-contact-cell';
import InteractionDateCell from 'apps/interactions/components/cells/interaction-date-cell';
import InteractionTypeCell from 'apps/interactions/components/cells/interaction-type-cell';

export const createInteractionTableColumns = ({ highlight, showContact, showFullText, showMessage, contactId }) => [
  {
    field: 'interaction_type',
    headerName: 'Type',
    description: 'The type of interaction (call, email, meeting, etc.)',
    sortable: false,
    renderCell: (params) => (params.row ? <InteractionTypeCell interaction={params.row} /> : null),
    maxWidth: 80,
  },
  {
    field: 'interaction',
    headerName: 'Interaction',
    description: 'The main interaction content including notes, subject, and details',
    flex: 1,
    sortable: false,
    hideable: false,
    renderCell: (params) =>
      params.row ? (
        <Interaction
          className={classNames('note-feed-item')}
          props={{
            interaction: params.row,
            highlight,
            showContact,
            showFullText,
            showMessage,
            contactId,
          }}
          clickable={true}
        />
      ) : null,
  },
  {
    field: 'date_occurred',
    headerName: 'Date',
    description: 'When the interaction occurred',
    renderCell: (params) => (params.row ? <InteractionDateCell interaction={params.row} /> : null),
    minWidth: 120,
  },
  {
    field: 'contact_id',
    headerName: 'Contact',
    description: 'The constituent associated with this interaction',
    sortable: false,
    renderCell: (params) => (params.row ? <InteractionContactCell interaction={params.row} /> : null),
  },
  {
    field: 'author_name',
    headerName: 'Author',
    description: 'The person who created or recorded this interaction',
    sortable: false,
  },
];
